import { useBoundStore } from '@/store';
import { HelpCircle } from '@alchemer/ui/icons';
import { ErrorBoundary } from '@sentry/react';
import { AlchemerApp } from '@alchemer/unified-platform';
import { SharedComponentsProvider } from '@alchemer/unified-platform/sharedComponentsProvider';
import type { AppConfig, NavigationHandlerRoute } from '@alchemer/unified-platform/sharedComponentsProvider';
import { TopNavigation } from '@alchemer/unified-platform/topNavigation';
import { Logo } from '@alchemer/unified-platform/logo';
import { CreateButton as UpCreateButton } from '@alchemer/unified-platform/createButton';
import { Notifications } from '@alchemer/unified-platform/notifications';
import { Help } from '@alchemer/unified-platform/help';
import { Profile } from '@alchemer/unified-platform/profile';
import { LeftNavigation } from '@alchemer/unified-platform/leftNavigation';
import { WaffleMenu } from '@alchemer/unified-platform/waffleMenu';
import { useLogout } from '@/hooks/useLogout';
import { useRegionChange } from '@/hooks/useRegionChange';
import { Search } from '@alchemer/unified-platform/search';
import { useTopHeaderHeight } from '@/hooks/useTopHeaderHeight';
import { tw } from '@/lib/tailwind';
import { useIsCreator } from '@/api/queries/useSdkTokenQuery';
import NavigationItems from '@components/TopHeader/NavigationItems/NavigationItems';
import { CreateButton } from './CreateButton/CreateButton';
import UniNav from './UniNav/UniNav';
import UserAvatar from './UserAvatar/UserAvatar';
import { ImpersonationBanner } from './ImpersonationBanner/ImpersonationBanner';
import ImpersonateUserDialog from './ImpersonateUserDialog/ImpersonateUserDialog';
import UserRegion from './UserRegion/UserRegion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFlag } from '@alchemer/unleash';
import { AUTH_CONFIG } from '@/lib/auth0';
import { FEATURE_FLAGS } from '@/config/features';
import { useNavigate } from '@tanstack/react-router';
import { isRegion } from '@/constants/regions';

// Refresh the Auth0 access token on the same cadence the rest of the app uses
// (see useInactivityLogout); the unified-platform provider needs a non-expired JWT.
const TOKEN_REFRESH_INTERVAL_MS = Number(import.meta.env.VITE_AUTH0_TOKEN_REFRESH_MS) || 10 * 60 * 1000;

export default function TopHeader() {
  const isCreator = useIsCreator(); // Get from React Query
  const headerRef = useTopHeaderHeight();
  const isUnifiedIntegrationEnabled = useFlag(FEATURE_FLAGS.UNIFIED_INTEGRATION);

  const impersonatedUser = useBoundStore((state) => state.impersonatedUser);
  const exitImpersonation = useBoundStore((state) => state.exitImpersonation);

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    let active = true;
    const refreshToken = (forceRefresh = false) =>
      getAccessTokenSilently({
        authorizationParams: AUTH_CONFIG,
        cacheMode: forceRefresh ? 'off' : 'on',
      })
        .then((next) => {
          if (active) setToken(next);
        })
        .catch(() => {
          // Keep the last good token; useFetchSharedData tolerates a stale/empty token.
        });

    void refreshToken();
    const intervalId = setInterval(() => void refreshToken(true), TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  const selectedRegion = useBoundStore((state) => state.selectedRegion);
  const unifiedPlatformApiBaseUrl = useBoundStore((state) => state.getCurrentUnifiedPlatformApiUrl());
  const navigate = useNavigate();
  const changeRegion = useRegionChange();

  const [isImpersonateDialogOpen, setIsImpersonateDialogOpen] = useState(false);

  const closeImpersonateDialog = useCallback(() => {
    // Drop the transient `impersonate_modal` flag that the unified-platform "Impersonate"
    // action appended, so it doesn't linger in the URL after the modal is dismissed.
    const url = new URL(window.location.href);
    if (url.searchParams.has('impersonate_modal')) {
      url.searchParams.delete('impersonate_modal');
      window.history.replaceState(null, '', url.toString());
    }
    setIsImpersonateDialogOpen(false);
  }, []);

  const navigationHandler = useCallback(
    (route: NavigationHandlerRoute) => {
      // The provider already applied any `append_query` URL update before this handler runs.
      // The only such action the dashboard responds to is "Impersonate" (impersonate_modal=true),
      // which opens the impersonation modal; everything else is fully handled by the URL update.
      if (route.action === 'append_query') {
        if (route.action_config?.params?.impersonate_modal) {
          setIsImpersonateDialogOpen(true);
        }
        return;
      }

      const { action_config } = route;
      if (!action_config) return;

      const { base_url, path, params } = action_config;

      try {
        const url = new URL(base_url);
        url.pathname = path;

        if (params) {
          for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
        }

        // Region switch: the unified-platform nav appends a `datacenter=<region>` query param
        // to many links (left nav, etc.), not just the region picker — so a datacenter value
        // only means "switch regions" when it names a known region that differs from the one
        // we're already in. In that case route through the dashboard's region-change flow
        // (persist region, clear the region-scoped SDK token + React Query cache, then
        // hard-navigate) so the destination region initializes cleanly; otherwise fall through
        // to normal navigation.
        const datacenterParam = url.searchParams.get('datacenter');
        const targetRegion = isRegion(datacenterParam) ? datacenterParam : null;
        if (targetRegion && targetRegion !== selectedRegion) {
          changeRegion(targetRegion, url.toString());
          return;
        }

        if (url.origin === window.location.origin) {
          // in-SPA; on a same-route click bump `reload` so the TS embed re-navigates (e.g. exits Spark)
          const isSameRoute = url.pathname === window.location.pathname;
          navigate({ to: path, search: isSameRoute ? { ...params, reload: Date.now() } : (params ?? undefined) });
        } else {
          // cross-app
          window.location.assign(url.toString());
        }
      } catch (err) {
        console.error('[TopHeader] navigation failed', { base_url, path }, err);
      }
    },
    [navigate, changeRegion, selectedRegion],
  );

  const handleLogout = useLogout();

  const sharedComponentsContext = useMemo<AppConfig>(
    () => ({
      app: AlchemerApp.DASHBOARD,
      region: selectedRegion,
      token: token ?? '',
      baseUrl: unifiedPlatformApiBaseUrl,
      navigationHandler,
      ...(impersonatedUser && { email: impersonatedUser.email }),
    }),
    [selectedRegion, token, unifiedPlatformApiBaseUrl, navigationHandler, impersonatedUser],
  );

  return (
    <ErrorBoundary>
      <div ref={headerRef}>
        {isUnifiedIntegrationEnabled ? (
          <SharedComponentsProvider context={sharedComponentsContext}>
            <TopNavigation
              LeftNavigation={LeftNavigation}
              WaffleMenu={WaffleMenu}
              Logo={Logo}
              Search={Search}
              CreateButton={UpCreateButton}
              Notifications={Notifications}
              Help={Help}
              Profile={<Profile handleLogout={handleLogout} />}
            />
            <ImpersonateUserDialog
              isOpen={isImpersonateDialogOpen}
              onClose={closeImpersonateDialog}
            />
            {impersonatedUser && (
              <ImpersonationBanner
                userName={impersonatedUser.name}
                onExit={exitImpersonation}
              />
            )}
          </SharedComponentsProvider>
        ) : (
          <>
            <section
              className={tw(
                'text-background-400 flex h-[4.125em] shrink-0',
                'items-center justify-between gap-3 bg-white px-4 py-2',
              )}
            >
              <UniNav />
              <div className="text-background-600 flex-1">Alchemer Clarity</div>
              <div className="flex items-center gap-2">
                {isCreator && <CreateButton />}
                <a
                  aria-label="Help"
                  href="https://help.alchemer.com/help/dashboards"
                  target="_blank"
                  rel="noreferrer"
                  className={tw(
                    'bg-background-100 hover:bg-background-200 flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                  )}
                >
                  <HelpCircle
                    height={17}
                    width={17}
                    className="text-background-500"
                  />
                </a>
                <UserRegion />
                <UserAvatar />
              </div>
            </section>
            <nav
              className={tw(
                "after:bg-background-200 relative bg-white pl-4 after:absolute after:bottom-0 after:left-0 after:z-10 after:h-px after:w-full after:content-['']",
              )}
            >
              <NavigationItems />
            </nav>
            {impersonatedUser && (
              <ImpersonationBanner
                userName={impersonatedUser.name}
                onExit={exitImpersonation}
              />
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
